import { has } from 'lodash'
/**
 * Deep sanitizes an object by setting specified properties to empty strings at any nesting level
 * @param obj - The object to sanitize (can contain nested objects, arrays, and primitives)
 * @param propsToRemove - Array of property names to set to empty strings
 * @returns A new sanitized object with specified properties set to empty strings
 */
const sanitizeByFlatPath = <T>(obj: T, propsToRemove: readonly string[]): T => {
  // Handle null, undefined, or primitive values
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj
  }

  // Handle arrays - recursively sanitize each element
  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeByFlatPath(item, propsToRemove)) as T
  }

  // Handle objects - create a new object with specified properties set to empty strings
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    // Set properties to empty string if they should be sanitized
    if (propsToRemove.includes(key)) {
      sanitized[key] = ''
      continue
    }

    // Recursively sanitize nested values
    sanitized[key] = sanitizeByFlatPath(value, propsToRemove)
  }

  return sanitized as T
}

const sanitizeByDotNotation = <T>(obj: T, propsToRemove: readonly string[]): T => {
  // Handle null, undefined, or primitive values
  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return obj
  }

  if (propsToRemove.length > 0) {
    propsToRemove.forEach((prop) => {
      const [parent, ...child]: [string, ...string[]] = prop.split('.') as [string, ...string[]]
      if (has(obj, parent)) {
        if (child.length === 0) {
          ;(obj as Record<string, unknown>)[parent] = ''
          return
        }

        sanitizeByDotNotation(obj[parent], [child.join('.')])
      }
    })
  }

  return obj
}

const sanitizeData = <T>(obj: T, propsToRemove: readonly string[]): T => {
  const sanitizedObj = sanitizeByDotNotation(
    obj,
    propsToRemove.filter((prop) => prop.includes('.')),
  )
  return sanitizeByFlatPath(
    sanitizedObj,
    propsToRemove.filter((prop) => !prop.includes('.')),
  )
}

export default sanitizeData
